"use client"

import { useEffect, useMemo, useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { CircleNotch as Loader2, CheckCircle as CheckCircle2, ArrowsClockwise as RefreshCw, Trash as Trash2 } from "@phosphor-icons/react"
import { useToast } from "@/hooks/use-toast"
import { apiRequestWithAuth, getStoredToken } from "@/lib/api-config"

interface BuyOrderVO {
  billId: number
  orderNo: string
  account: string
  userName: string
  packageKey: string
  group: string
  groupLabel: string
  duration: string
  durationLabel: string
  days: number
  price: number
  packageName: string
  description: string
  payType: "wechat" | "alipay"
  payTypeLabel: string
  state: 0 | 1 | 2
  stateLabel: string
  qrCodeUrl: string
  createTime: string
  confirmTime: string
  shipTime: string
}

const stateBadge = (state: number, label: string) => {
  if (state === 0)
    return (
      <span className="inline-block px-2 py-1 text-xs rounded bg-yellow-100 text-yellow-700">
        {label}
      </span>
    )
  if (state === 1)
    return (
      <span className="inline-block px-2 py-1 text-xs rounded bg-blue-100 text-blue-700">
        {label}
      </span>
    )
  return (
    <span className="inline-block px-2 py-1 text-xs rounded bg-green-100 text-green-700">
      {label}
    </span>
  )
}

export default function OrdersManagementPage() {
  const { toast } = useToast()
  const [orders, setOrders] = useState<BuyOrderVO[]>([])
  const [loading, setLoading] = useState(false)
  const [shippingId, setShippingId] = useState<number | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [batchBusy, setBatchBusy] = useState(false)
  const [pagination, setPagination] = useState({ current: 1, size: 5 })
  const [confirmAction, setConfirmAction] = useState<
    { type: "ship" | "delete" | "batchShip" | "batchDelete"; billId?: number } | null
  >(null)

  const fetchOrders = async () => {
    const token = getStoredToken()
    if (!token) return
    setLoading(true)
    try {
      const result = (await apiRequestWithAuth("/showPendingOrders", token, { method: "GET" })) as {
        code: number
        msg?: string
        data?: BuyOrderVO[]
      }
      if (result.code === 200 && result.data) {
        setOrders(result.data)
        setSelectedIds(new Set()) // 列表刷新后清空选择
        setPagination((prev) => ({ ...prev, current: 1 }))
      } else {
        toast({ variant: "destructive", title: "获取订单失败", description: result.msg })
      }
    } catch {
      toast({ variant: "destructive", title: "网络错误" })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchOrders()
  }, [])

  const shipOrder = async (billId: number) => {
    const token = getStoredToken()
    if (!token) return
    setShippingId(billId)
    try {
      const result = (await apiRequestWithAuth("/markShipped", token, {
        method: "POST",
        body: JSON.stringify({ billId }),
      })) as { code: number; msg?: string }
      if (result.code === 200) {
        toast({ variant: "success", title: "发货成功", description: result.msg })
        fetchOrders()
      } else {
        toast({ variant: "destructive", title: "发货失败", description: result.msg })
      }
    } catch {
      toast({ variant: "destructive", title: "网络错误" })
    } finally {
      setShippingId(null)
    }
  }

  const deleteOrders = async (billIds: number[]) => {
    const token = getStoredToken()
    if (!token) return
    try {
      const result = (await apiRequestWithAuth("/deletePendingOrders", token, {
        method: "POST",
        body: JSON.stringify({ billIds }),
      })) as { code: number; msg?: string }
      if (result.code === 200) {
        toast({ variant: "success", title: "删除成功", description: result.msg })
        fetchOrders()
      } else {
        toast({ variant: "destructive", title: "删除失败", description: result.msg })
      }
    } catch {
      toast({ variant: "destructive", title: "网络错误" })
    }
  }

  // 仅 state<2（未发货）可被选中：state=0 可删可发，state=1 仅可发
  const selectableIds = useMemo(
    () => new Set(orders.filter((o) => o.state < 2).map((o) => o.billId)),
    [orders],
  )
  const allSelected =
    selectableIds.size > 0 && Array.from(selectableIds).every((id) => selectedIds.has(id))
  const someSelected = selectedIds.size > 0

  const toggleSelect = (billId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(billId)) next.delete(billId)
      else next.add(billId)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(selectableIds))
    }
  }

  // 客户端分页：与其他管理页保持“上一页 / 第 x / y 页 / 下一页”的统一节奏
  const totalPages = Math.max(1, Math.ceil(orders.length / pagination.size))
  const displayedOrders = useMemo(
    () =>
      orders.slice(
        (pagination.current - 1) * pagination.size,
        pagination.current * pagination.size,
      ),
    [orders, pagination],
  )
  useEffect(() => {
    if (pagination.current > totalPages) {
      setPagination((prev) => ({ ...prev, current: totalPages }))
    }
  }, [pagination.current, totalPages])

  // 确认弹窗分发：替代原生 confirm，文案与语义保持一致
  const confirmMeta = (() => {
    switch (confirmAction?.type) {
      case "ship":
        return {
          title: "确认已收到款并发货？",
          description: "此操作会为用户增加对应天数。",
          danger: false,
        }
      case "delete":
        return {
          title: "确认删除这个待付款订单？",
          description: "删除后不可恢复。",
          danger: true,
        }
      case "batchShip":
        return {
          title: `确认批量发货选中的 ${selectedIds.size} 个订单？`,
          description: "仅待发货（已点击付款）的订单会被处理。",
          danger: false,
        }
      case "batchDelete":
        return {
          title: `确认删除选中的 ${selectedIds.size} 个订单？`,
          description: "仅待付款订单会被删除，删除后不可恢复。",
          danger: true,
        }
      default:
        return null
    }
  })()

  const handleConfirm = async () => {
    if (!confirmAction) return
    const { type, billId } = confirmAction
    setConfirmAction(null)
    if (type === "ship" && billId != null) await shipOrder(billId)
    else if (type === "delete" && billId != null) await deleteOrders([billId])
    else if (type === "batchShip") await handleBatchShip()
    else if (type === "batchDelete") await handleBatchDelete()
  }

  const handleBatchDelete = async () => {
    const token = getStoredToken()
    if (!token) return
    setBatchBusy(true)
    try {
      const result = (await apiRequestWithAuth("/deletePendingOrders", token, {
        method: "POST",
        body: JSON.stringify({ billIds: Array.from(selectedIds) }),
      })) as { code: number; msg?: string }
      if (result.code === 200) {
        toast({ variant: "success", title: "批量删除完成", description: result.msg })
        fetchOrders()
      } else {
        toast({ variant: "destructive", title: "删除失败", description: result.msg })
      }
    } catch {
      toast({ variant: "destructive", title: "网络错误" })
    } finally {
      setBatchBusy(false)
    }
  }

  const handleBatchShip = async () => {
    setBatchBusy(true)
    let okCount = 0
    let failCount = 0
    for (const billId of Array.from(selectedIds)) {
      const order = orders.find((o) => o.billId === billId)
      if (!order || order.state !== 1) continue
      try {
        const token = getStoredToken()
        if (!token) continue
        const result = (await apiRequestWithAuth("/markShipped", token, {
          method: "POST",
          body: JSON.stringify({ billId }),
        })) as { code: number; msg?: string }
        if (result.code === 200) okCount++
        else failCount++
      } catch {
        failCount++
      }
    }
    setBatchBusy(false)
    toast({
      variant: okCount > 0 ? "success" : "destructive",
      title: `批量发货完成`,
      description: `成功 ${okCount}，失败 ${failCount}`,
    })
    fetchOrders()
  }

  return (
      <div className="space-y-6">
        {/* 批量操作工具栏：有选中时才显示 */}
        {someSelected && (
          <div
            style={{
              border: "1.5px dashed var(--ember-400)",
              borderRadius: 12,
              padding: "10px 16px",
              background: "rgba(255, 240, 232, 0.6)",
              color: "var(--ember-600)",
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontWeight: 600 }}>已选 {selectedIds.size} 个订单</span>
            <Button
              size="sm"
              disabled={batchBusy}
              onClick={() => setConfirmAction({ type: "batchShip" })}
              className="bg-green-500 hover:bg-green-600 text-white"
            >
              <CheckCircle2 className="mr-1 h-3 w-3" />
              批量发货
            </Button>
            <Button
              size="sm"
              disabled={batchBusy}
              onClick={() => setConfirmAction({ type: "batchDelete" })}
              style={{
                background: "rgba(193,69,69,0.15)",
                borderColor: "var(--berry-500)",
                color: "var(--berry-500)",
              }}
            >
              <Trash2 className="mr-1 h-3 w-3" />
              批量删除
            </Button>
            <Button size="sm" variant="outline" onClick={() => setSelectedIds(new Set())}>
              取消选择
            </Button>
          </div>
        )}

        <Card
          style={{
            border: "1.5px solid var(--ember-400)",
            borderRadius: 16,
            background:
              "radial-gradient(circle, rgba(229,146,102,0.18) 1.5px, transparent 1.5px), radial-gradient(circle, rgba(255,190,150,0.12) 1px, transparent 1px), var(--ember-50)",
            backgroundSize: "28px 28px, 14px 14px",
            backgroundPosition: "0 0, 7px 7px",
            color: "var(--ember-600)",
          }}
        >
          <CardHeader className="admin-orders-card-header">
            <CardTitle className="text-ember-600">待处理订单 ({orders.length})</CardTitle>
            <Button size="sm" onClick={fetchOrders} disabled={loading} variant="outline">
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              刷新
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-8 text-soil-600">暂无待处理订单 🎉</div>
            ) : (
              <Table className="mobile-order-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-soil-600" style={{ width: 40 }}>
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                        style={{ cursor: "pointer" }}
                      />
                    </TableHead>
                    <TableHead className="text-soil-600">订单号</TableHead>
                    <TableHead className="text-soil-600">用户</TableHead>
                    <TableHead className="text-soil-600">套餐</TableHead>
                    <TableHead className="text-soil-600">金额</TableHead>
                    <TableHead className="text-soil-600">支付方式</TableHead>
                    <TableHead className="text-soil-600">状态</TableHead>
                    <TableHead className="text-soil-600">下单时间</TableHead>
                    <TableHead className="text-soil-600">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayedOrders.map((o) => {
                    const selectable = o.state < 2
                    return (
                      <TableRow key={o.billId} className="border-ember-400">
                        <TableCell data-label="选择">
                          {selectable ? (
                            <input
                              type="checkbox"
                              checked={selectedIds.has(o.billId)}
                              onChange={() => toggleSelect(o.billId)}
                              style={{ cursor: "pointer" }}
                            />
                          ) : null}
                        </TableCell>
                        <TableCell data-label="订单号" className="font-mono text-xs text-ember-600">{o.orderNo}</TableCell>
                        <TableCell data-label="用户" className="text-ember-600">
                          <div>{o.account}</div>
                          {o.userName && <div className="text-xs text-gray-500">{o.userName}</div>}
                        </TableCell>
                        <TableCell data-label="套餐" className="text-ember-600">
                          {o.packageName}×1（{o.days}天）
                        </TableCell>
                        <TableCell data-label="金额" className="font-semibold text-cyan-500">¥{o.price}</TableCell>
                        <TableCell data-label="支付方式" className="text-ember-600">{o.payTypeLabel}</TableCell>
                        <TableCell data-label="状态">{stateBadge(o.state, o.stateLabel)}</TableCell>
                        <TableCell data-label="下单时间" className="text-xs text-soil-600">
                          <div>{o.createTime}</div>
                          {o.confirmTime && <div className="text-blue-500">已点击付款：{o.confirmTime}</div>}
                        </TableCell>
                        <TableCell data-label="操作">
                          <div className="flex flex-col gap-1">
                            {o.state === 0 && (
                              <Button
                                size="sm"
                                onClick={() => setConfirmAction({ type: "delete", billId: o.billId })}
                                style={{
                                  background: "rgba(193,69,69,0.15)",
                                  borderColor: "var(--berry-500)",
                                  color: "var(--berry-500)",
                                }}
                              >
                                <Trash2 className="mr-1 h-3 w-3" />
                                删除
                              </Button>
                            )}
                            {o.state < 2 ? (
                              <Button
                                size="sm"
                                disabled={shippingId === o.billId}
                                onClick={() => setConfirmAction({ type: "ship", billId: o.billId })}
                                className="bg-green-500 hover:bg-green-600 text-white"
                              >
                                {shippingId === o.billId ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <>
                                    <CheckCircle2 className="mr-1 h-3 w-3" />
                                    确认发货
                                  </>
                                )}
                              </Button>
                            ) : (
                              <span className="text-xs text-green-500">✓ 已发货</span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            )}
            {orders.length > 0 && (
              <div className="responsive-pagination pt-4">
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={pagination.current <= 1}
                    onClick={() => setPagination((prev) => ({ ...prev, current: prev.current - 1 }))}
                  >
                    上一页
                  </Button>
                  <span aria-live="polite">第 {pagination.current} / {totalPages} 页</span>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={pagination.current >= totalPages}
                    onClick={() => setPagination((prev) => ({ ...prev, current: prev.current + 1 }))}
                  >
                    下一页
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <details className="admin-orders-tips">
          <summary>操作提示</summary>
          <div className="admin-orders-tips__body">
            <p>客户提交订单后，企业微信机器人会推送通知给你。</p>
            <p>客户点击"我已付款"后，会再推送一条提醒。</p>
            <p>请确认微信/支付宝零钱或余额到账后，再点"确认发货"为用户加时长。</p>
            <p>顶部复选框可多选订单，进行批量发货或批量删除（仅删除待付款订单）。</p>
          </div>
        </details>

        <AlertDialog
          open={confirmAction !== null}
          onOpenChange={(open) => {
            if (!open) setConfirmAction(null)
          }}
        >
          <AlertDialogContent style={{ background: "var(--cream-50)" }}>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-ember-600">{confirmMeta?.title}</AlertDialogTitle>
              <AlertDialogDescription className="text-soil-600">
                {confirmMeta?.description}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-ember-300 text-ember-600">取消</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirm}
                className={
                  confirmMeta?.danger
                    ? "bg-red-600 hover:bg-red-700 text-white"
                    : "bg-green-600 hover:bg-green-700 text-white"
                }
              >
                确认
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
  )
}
